import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { SessionContext } from "../../../contexts";
import { TGetPatientImageData, getPatientImages } from "../../../services/api";
import ImageComparisonSlider from "../../uncommon/web/image-comparison-slider/ImageComparisonSlider";

const tokens = {
  color: {
    text: "#0F172A",
    muted: "#64748B",
    surface: "#F8FAFC",
    border: "#E2E8F0",
    focus: "#2563EB",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
  },
  shadow: {
    none: 0,
  },
  font: {
    base: "System",
  },
  fontSize: {
    sm: 14,
    md: 16,
  },
  lineHeight: {
    sm: 20,
    md: 24,
  },
  zIndex: {
    base: 1,
  },
  motion: {
    microMs: 100,
    standardMs: 200,
    complexMs: 300,
  },
  size: {
    simulationMaxWidth: 320,
  },
};

const SIMULATION_UPLOAD_TYPES = [
  "TransformImageForForm",
  "TransformImageForUser",
];

const SimulationGalleryView: React.FC<{
  patientUuid: string;
}> = ({ patientUuid }) => {
  const session = useContext(SessionContext)?.session;
  const [simulations, setSimulations] = useState<TGetPatientImageData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSimulations = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await getPatientImages(session, patientUuid, {
        uploadTypes: SIMULATION_UPLOAD_TYPES,
      });
      setSimulations(response);
    } catch (err) {
      setError("Unable to load simulations.");
    } finally {
      setIsLoading(false);
    }
  }, [session, patientUuid]);

  useEffect(() => {
    setSimulations([]);
    setError(null);
  }, [patientUuid]);

  useEffect(() => {
    fetchSimulations();
  }, [fetchSimulations]);

  const simulationCards = useMemo(() => {
    const sorted = [...simulations].sort((a, b) => {
      const aDate = a.image_data?.created_at
        ? new Date(a.image_data.created_at).getTime()
        : 0;
      const bDate = b.image_data?.created_at
        ? new Date(b.image_data.created_at).getTime()
        : 0;
      return bDate - aDate;
    });

    return sorted.filter((item) => item.presigned_urls?.length > 0);
  }, [simulations]);

  const renderStatus = () => {
    if (isLoading) {
      return (
        <View style={styles.loadingRow}>
          <ActivityIndicator color={tokens.color.focus} />
          <Text style={styles.statusText}>Loading simulations...</Text>
        </View>
      );
    }

    if (error) {
      return <Text style={styles.statusText}>{error}</Text>;
    }

    if (simulationCards.length === 0) {
      return <Text style={styles.statusText}>No simulations yet.</Text>;
    }

    return null;
  };

  return (
    <View style={styles.container}>
      {renderStatus()}

      {simulationCards.map((simulation) => (
        <View key={simulation.image_data?.id} style={styles.card}>
          <View style={styles.sliderWrap}>
            <View style={styles.sliderInner}>
              <ImageComparisonSlider
                imageUrls={simulation.presigned_urls}
                metaData={{}}
                adjustText="Adjust the output"
              />
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: tokens.spacing.lg,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.sm,
  },
  statusText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  card: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: tokens.color.border,
    backgroundColor: tokens.color.surface,
    padding: tokens.spacing.md,
  },
  sliderWrap: {
    width: "100%",
  },
  sliderInner: {
    width: "100%",
    maxWidth: tokens.size.simulationMaxWidth,
    alignSelf: "center",
  },
});

export default SimulationGalleryView;
